import { pickT } from "@repo/shared";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { T } from "./financial-situation-config-form.i18n";

type FinancialSituationConfigFormProps = {
  flowLanguage: string;
  showIncome: boolean;
  onShowIncomeChange: (show: boolean) => void;
  showAssets: boolean;
  onShowAssetsChange: (show: boolean) => void;
  showLiabilities: boolean;
  onShowLiabilitiesChange: (show: boolean) => void;
  showCommitments: boolean;
  onShowCommitmentsChange: (show: boolean) => void;
  showCapacitySection: boolean;
  onShowCapacitySectionChange: (show: boolean) => void;
  /** QL-266 — false when the flow's model enables capacity for no client type. */
  capacityConfiguredInModel: boolean;
  showStability: boolean;
  onShowStabilityChange: (show: boolean) => void;
  showExpectingChanges: boolean;
  onShowExpectingChangesChange: (show: boolean) => void;
  showCapacity: boolean;
  onShowCapacityChange: (show: boolean) => void;
  showSummary: boolean;
  onShowSummaryChange: (show: boolean) => void;
};

export function FinancialSituationConfigForm({
  flowLanguage,
  showIncome,
  onShowIncomeChange,
  showAssets,
  onShowAssetsChange,
  showLiabilities,
  onShowLiabilitiesChange,
  showCommitments,
  onShowCommitmentsChange,
  showCapacitySection,
  onShowCapacitySectionChange,
  capacityConfiguredInModel,
  showStability,
  onShowStabilityChange,
  showExpectingChanges,
  onShowExpectingChangesChange,
  showCapacity,
  onShowCapacityChange,
  showSummary,
  onShowSummaryChange,
}: FinancialSituationConfigFormProps) {
  const t = pickT(T, flowLanguage);

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
      <div className="space-y-2">
        <Label className="text-base">{t.sectionVisibility}</Label>
        <p className="text-sm text-muted-foreground">{t.sectionVisibilityDesc}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center justify-between p-2 border rounded">
          <Label htmlFor="finsit-show-income">{t.income}</Label>
          <Switch
            id="finsit-show-income"
            checked={showIncome}
            onCheckedChange={onShowIncomeChange}
          />
        </div>
        <div className="flex items-center justify-between p-2 border rounded">
          <Label htmlFor="finsit-show-assets">{t.assets}</Label>
          <Switch
            id="finsit-show-assets"
            checked={showAssets}
            onCheckedChange={onShowAssetsChange}
          />
        </div>
        <div className="flex items-center justify-between p-2 border rounded">
          <Label htmlFor="finsit-show-liabilities">{t.liabilities}</Label>
          <Switch
            id="finsit-show-liabilities"
            checked={showLiabilities}
            onCheckedChange={onShowLiabilitiesChange}
          />
        </div>
        <div className="flex items-center justify-between p-2 border rounded">
          <Label htmlFor="finsit-show-commitments">{t.commitments}</Label>
          <Switch
            id="finsit-show-commitments"
            checked={showCommitments}
            onCheckedChange={onShowCommitmentsChange}
          />
        </div>
        {/* QL-266/QL-364 — the section is model-gated: when the model's
            investment capacity calculator is off for every client type the
            widget never renders it, so the toggle is not offered at all. */}
        {capacityConfiguredInModel && (
          <div className="flex items-center justify-between p-2 border rounded">
            <Label htmlFor="finsit-show-capacity-section">{t.capacity}</Label>
            <Switch
              id="finsit-show-capacity-section"
              checked={showCapacitySection}
              onCheckedChange={onShowCapacitySectionChange}
            />
          </div>
        )}
      </div>

      <div className="space-y-2 pt-2 border-t">
        <Label className="text-base">{t.displayOptions}</Label>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="finsit-show-stability">{t.incomeStabilityQuestion}</Label>
          <p className="text-xs text-muted-foreground">{t.incomeStabilityDesc}</p>
        </div>
        <Switch
          id="finsit-show-stability"
          checked={showStability}
          onCheckedChange={onShowStabilityChange}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="finsit-show-expecting-changes">{t.expectingChangesQuestion}</Label>
          <p className="text-xs text-muted-foreground">{t.expectingChangesDesc}</p>
        </div>
        <Switch
          id="finsit-show-expecting-changes"
          checked={showExpectingChanges}
          onCheckedChange={onShowExpectingChangesChange}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="finsit-show-capacity">{t.capacityForLoss}</Label>
          <p className="text-xs text-muted-foreground">{t.capacityForLossDesc}</p>
        </div>
        <Switch
          id="finsit-show-capacity"
          checked={showCapacity}
          onCheckedChange={onShowCapacityChange}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="finsit-show-summary">{t.summaryMetrics}</Label>
          <p className="text-xs text-muted-foreground">{t.summaryMetricsDesc}</p>
        </div>
        <Switch
          id="finsit-show-summary"
          checked={showSummary}
          onCheckedChange={onShowSummaryChange}
        />
      </div>

      <div className="text-sm text-muted-foreground p-3 border rounded-md bg-muted/50">
        <p className="font-medium mb-1">{t.note}</p>
        <p className="text-xs">{t.financialSituationNote}</p>
      </div>
    </div>
  );
}
